import { ListSubheader, MenuItem, TextField, Tooltip } from '@mui/material'
import Grid from '@mui/material/Grid'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import NewSwitch from 'ui/atoms/NewSwitch/NewSwitch'
import FormRow from 'ui/components/FormRow/FormRow'
import type { BidProductionConsumption, BidTagSelectionGroup } from '../DayAheadBidEditor.types'

type BidTagSelectionProps = {
  productionConsumption: BidProductionConsumption
  selectedTagId: number | null
  groupedTagOptions: BidTagSelectionGroup[]
  hasAvailableTagsForSelectedType: boolean
  tagValidationError?: string
  onProductionConsumptionChange: (value: BidProductionConsumption) => void
  onTagChange: (value: string) => void
}

export function BidTagSelection({
  productionConsumption,
  selectedTagId,
  groupedTagOptions,
  hasAvailableTagsForSelectedType,
  tagValidationError,
  onProductionConsumptionChange,
  onTagChange,
}: BidTagSelectionProps): ReactNode {
  const { t } = useTranslation()

  return (
    <FormRow>
      <Grid size={{ xs: 12 }} className="flex items-center gap-3">
        <NewSwitch
          value={productionConsumption}
          items={[
            { label: t('Production'), value: 'production' },
            { label: t('Consumption'), value: 'consumption' },
          ]}
          onClick={(value) => onProductionConsumptionChange(value as BidProductionConsumption)}
        />
        <TextField
          id={'tag'}
          select
          label={t('Tag')}
          value={selectedTagId ?? ''}
          className="flex-1"
          error={Boolean(tagValidationError)}
          helperText={
            tagValidationError ||
            (!hasAvailableTagsForSelectedType ? t('No tags match the selected subtype') : undefined)
          }
          onChange={(event) => onTagChange(event.target.value)}
        >
          <MenuItem value={''}>{t('Select tag')}</MenuItem>
          {groupedTagOptions.map((group) => {
            if (group.options.length === 0) {
              return null
            }

            return [
              <ListSubheader key={`${group.buySell}-header`} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {group.buySell === 'buy' ? t('Buy') : t('Sell')}
              </ListSubheader>,
              ...group.options.map((option) => {
                const menuItem = (
                  <MenuItem key={option.id} value={option.id} disabled={option.disabled}>
                    {option.tagKey}
                  </MenuItem>
                )

                if (option.disabled && option.id !== selectedTagId) {
                  return (
                    <Tooltip key={option.id} title={t('This tag is already in use')} placement="top-start" followCursor>
                      <span>{menuItem}</span>
                    </Tooltip>
                  )
                }

                return menuItem
              }),
            ]
          })}
        </TextField>
      </Grid>
    </FormRow>
  )
}
